import { useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useApp } from '@/store/useApp';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportProjectDialog({ open, onOpenChange }: Props) {
  const importProject = useApp((s) => s.importProject);
  const nameId = useId();
  const htmlId = useId();
  const cssId = useId();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cssFileRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState('');
  const [html, setHtml] = useState('');
  const [css, setCss] = useState('');
  const [busy, setBusy] = useState(false);

  function reset() {
    setName('');
    setHtml('');
    setCss('');
  }

  async function handleHtmlFile(file: File | undefined) {
    if (!file) return;
    const text = await file.text();
    setHtml(text);
  }

  async function handleCssFile(file: File | undefined) {
    if (!file) return;
    const text = await file.text();
    setCss(text);
  }

  async function handleImport() {
    if (!html.trim()) return;
    setBusy(true);
    try {
      const payload: { html: string; name?: string; css?: string } = { html };
      if (name.trim()) payload.name = name.trim();
      if (css.trim()) payload.css = css;
      await importProject(payload);
      reset();
      onOpenChange(false);
    } catch (err) {
      // The import path is mostly local-only, but selfContainHtml does network
      // fetches which can fail in unexpected ways — surface so the user knows.
      // eslint-disable-next-line no-console
      console.error('Import failed', err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="text-lg font-semibold">Import HTML / CSS</SheetTitle>
          <SheetDescription className="text-sm text-neutral-500">
            Bring an existing design in as the starting artifact. Chat refines it from
            there — Claude sees the current HTML on every turn, so edits stay
            section-by-section instead of regenerating from scratch.
            <br />
            <strong>Self-contained HTML?</strong> Just paste it in the HTML field and
            leave CSS empty — full <code>&lt;!doctype html&gt;</code> documents pass
            through unchanged. External links (Google Fonts, Tailwind, three.js from
            cdnjs / jsdelivr / unpkg) are auto-resolved on import.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-2">
          <label htmlFor={nameId} className="text-sm font-medium">
            Project name (optional)
          </label>
          <Input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Imported HTML"
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label htmlFor={htmlId} className="text-sm font-medium">
              HTML
            </label>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="text-[11px] text-neutral-500 underline hover:text-neutral-700 dark:hover:text-neutral-300"
            >
              load .html file
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".html,.htm,text/html"
              hidden
              onChange={(e) => void handleHtmlFile(e.target.files?.[0])}
            />
          </div>
          <textarea
            id={htmlId}
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={10}
            placeholder="<!doctype html> ...   or just <main>...</main>"
            className="w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-900"
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label htmlFor={cssId} className="text-sm font-medium">
              CSS (optional — leave empty if HTML is already self-contained)
            </label>
            <button
              type="button"
              onClick={() => cssFileRef.current?.click()}
              className="text-[11px] text-neutral-500 underline hover:text-neutral-700 dark:hover:text-neutral-300"
            >
              load .css file
            </button>
            <input
              ref={cssFileRef}
              type="file"
              accept=".css,text/css"
              hidden
              onChange={(e) => void handleCssFile(e.target.files?.[0])}
            />
          </div>
          <textarea
            id={cssId}
            value={css}
            onChange={(e) => setCss(e.target.value)}
            rows={6}
            placeholder=":root { --primary: #1e293b; }"
            className="w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 font-mono text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-900"
          />
        </div>

        <SheetFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void handleImport()} disabled={!html.trim() || busy}>
            {busy ? 'Importing…' : 'Import as new project'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
